import React, { useState, useEffect } from 'react';
import { bookings as bookingsApi, auth } from '../api';
import { FiCalendar, FiPackage, FiUser, FiX, FiClock, FiEdit2, FiHash, FiSearch, FiLink } from 'react-icons/fi';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
import { bookingRecordsLabel } from '../utils/pluralRu';
import { PRICE_COST_WITHOUT_VAT } from '../constants/equipmentLabels';

function Bookings() {
  const [bookingsList, setBookingsList] = useState([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [myOnly, setMyOnly] = useState(false);
  const [loading, setLoading] = useState(true);
  const [editingBooking, setEditingBooking] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [currentUser, setCurrentUser] = useState(null);

  useEffect(() => {
    loadCurrentUser();
    loadBookings();
  }, [statusFilter, myOnly, searchQuery]);

  const loadCurrentUser = async () => {
    try {
      const user = await auth.getCurrentUser();
      setCurrentUser(user);
    } catch (error) {
      console.error('Error loading current user:', error);
    }
  };

  const loadBookings = async () => {
    setLoading(true);
    try {
      const bookings = await bookingsApi.getAll({
        status: statusFilter || undefined,
        q: searchQuery || undefined,
        my_only: myOnly,
      });
      setBookingsList(bookings);
    } catch (error) {
      console.error('Error loading bookings:', error);
    } finally {
      setLoading(false);
    }
  };

  const openEditModal = (booking) => {
    setEditingBooking(booking);
    setEditForm({
      quantity: booking.quantity,
      start_date: new Date(booking.start_date).toISOString().split('T')[0],
      end_date: new Date(booking.end_date).toISOString().split('T')[0],
      client_name: booking.client_name || '',
      deal_id: booking.deal_id ? String(booking.deal_id) : '',
      sale_price: booking.sale_price != null ? String(booking.sale_price) : '',
      notes: booking.notes || ''
    });
  };

  const handleUpdateBooking = async (e) => {
    e.preventDefault();
    try {
      await bookingsApi.update(editingBooking.id, {
        quantity: editForm.quantity,
        start_date: new Date(editForm.start_date).toISOString(),
        end_date: new Date(editForm.end_date).toISOString(),
        client_name: editForm.client_name,
        deal_id: editForm.deal_id || null,
        ...(editForm.sale_price !== ''
          ? { sale_price: parseFloat(String(editForm.sale_price).replace(',', '.')) }
          : {}),
        notes: editForm.notes
      });
      alert('Бронирование успешно обновлено!');
      setEditingBooking(null);
      setEditForm({});
      loadBookings();
    } catch (error) {
      alert(error.response?.data?.detail || 'Ошибка обновления бронирования');
    }
  };

  const handleRetryOnec = async (id) => {
    try {
      await bookingsApi.retryOnec(id);
      alert('Бронь отправлена в 1С ещё раз. Ответ появится в течение пары минут.');
      loadBookings();
    } catch (error) {
      alert(error.response?.data?.detail || 'Не удалось отправить бронь в 1С');
    }
  };

  const handleCancelBooking = async (id) => {
    if (!window.confirm('Вы уверены, что хотите отменить это бронирование?')) {
      return;
    }

    try {
      await bookingsApi.cancel(id);
      alert('Бронирование отменено');
      loadBookings();
    } catch (error) {
      alert(error.response?.data?.detail || 'Ошибка отмены бронирования');
    }
  };

  const getStatusBadge = (booking) => {
    const status = booking.status;
    const badges = {
      active: 'bg-green-100 text-green-800',
      completed: booking.auto_completed ? 'bg-indigo-100 text-indigo-800' : 'bg-blue-100 text-blue-800',
      cancelled: 'bg-red-100 text-red-800'
    };
    const labels = {
      active: 'Активно',
      completed: 'Завершено',
      cancelled: 'Отменено'
    };
    const label = booking.status_label || labels[status] || status;
    return (
      <div className="flex flex-col items-end gap-1">
        <span className={`inline-flex px-3 py-1 text-xs font-medium rounded-full ${badges[status] || 'bg-gray-100 text-gray-800'}`}>
          {label}
        </span>
        {getOnecBadge(booking)}
      </div>
    );
  };

  // Статус резерва в 1С. Пусто — бронь в 1С не отправлялась (обмен выключен или бронь
  // старше обмена), значок не рисуем.
  const getOnecBadge = (booking) => {
    const state = booking.onec_status;
    if (!state) return null;
    const look = {
      pending: ['bg-yellow-100 text-yellow-800', '1С: ждём ответа'],
      reserved: ['bg-green-100 text-green-800', `1С: резерв${booking.onec_order_number ? ` № ${booking.onec_order_number}` : ''}`],
      error: ['bg-red-100 text-red-800', '1С: ошибка'],
      released: ['bg-gray-100 text-gray-700', '1С: резерв снят'],
    }[state] || ['bg-gray-100 text-gray-700', `1С: ${state}`];
    return (
      <>
        <span className={`inline-flex px-3 py-1 text-xs font-medium rounded-full ${look[0]}`}>
          {look[1]}
        </span>
        {state === 'error' && booking.onec_error && (
          <span className="max-w-xs text-right text-xs text-red-700">{booking.onec_error}</span>
        )}
      </>
    );
  };

  const isUpcoming = (startDate) => {
    return new Date(startDate) > new Date();
  };

  const isCurrent = (startDate, endDate) => {
    const now = new Date();
    return new Date(startDate) <= now && new Date(endDate) >= now;
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Бронирования</h1>
        <p className="mt-2 text-sm text-gray-600">
          Все бронирования оборудования. Включите фильтр, чтобы видеть только свои.
        </p>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow mb-6 space-y-4">
        <div className="relative max-w-xl">
          <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Поиск: прибор, код 1С, ID сделки..."
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>

        <div className="flex items-center gap-4 flex-wrap">
          <span className="text-sm font-medium text-gray-700">Фильтр по статусу:</span>
          
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setStatusFilter('')}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                statusFilter === ''
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Все
            </button>
            <button
              onClick={() => setStatusFilter('active')}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                statusFilter === 'active'
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Активные
            </button>
            <button
              onClick={() => setStatusFilter('completed')}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                statusFilter === 'completed'
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Завершенные
            </button>
            <button
              onClick={() => setStatusFilter('cancelled')}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                statusFilter === 'cancelled'
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Отмененные
            </button>
          </div>

          <label className="inline-flex items-center gap-2 text-sm text-gray-700 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={myOnly}
              onChange={(e) => setMyOnly(e.target.checked)}
              className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            Только мои бронирования
          </label>

          <div className="ml-auto text-sm text-gray-600">
            Найдено: <strong>{bookingsList.length}</strong> {bookingRecordsLabel(bookingsList.length)}
          </div>
        </div>
      </div>

      {/* Bookings List */}
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="text-xl text-gray-600">Загрузка...</div>
        </div>
      ) : bookingsList.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <FiCalendar className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">Нет бронирований</h3>
          <p className="mt-1 text-sm text-gray-500">
            {statusFilter || searchQuery ? 'Попробуйте изменить фильтр или поисковый запрос' : 'Начните с просмотра оборудования'}
          </p>
        </div>
      ) : (
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="divide-y divide-gray-200">
            {bookingsList.map((booking) => (
              <div key={booking.id} className="p-4 sm:p-6 hover:bg-gray-50 transition-colors">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                          <FiPackage className="mr-2 text-primary-600" />
                          {booking.equipment.name}
                        </h3>
                        <p className="text-sm text-gray-600 ml-6">
                          {booking.equipment.vendor} {booking.equipment.model && `• ${booking.equipment.model}`}
                        </p>
                        {booking.equipment.code_1c && (
                          <p className="text-sm text-gray-600 ml-6 mt-1 flex items-center">
                            <FiHash className="mr-1 h-3.5 w-3.5" />
                            <span className="font-mono">{booking.equipment.code_1c}</span>
                          </p>
                        )}
                        {booking.equipment.price_ddp != null && booking.equipment.price_ddp !== '' && (
                          <p className="text-sm text-gray-900 ml-6 mt-1">
                            <strong>{PRICE_COST_WITHOUT_VAT}:</strong>{' '}
                            {Number(booking.equipment.price_ddp).toLocaleString('ru-RU')} ₽
                          </p>
                        )}
                        {booking.equipment.comment && (
                          <p className="text-sm text-gray-600 ml-6 mt-2 bg-gray-50 px-2 py-1 rounded">
                            <strong>Комментарий:</strong> {booking.equipment.comment}
                          </p>
                        )}
                        {booking.equipment.description && (
                          <p className="text-sm text-gray-500 ml-6 mt-1">
                            {booking.equipment.description}
                          </p>
                        )}
                      </div>
                      {getStatusBadge(booking)}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 ml-6">
                      <div className="space-y-2">
                        <div className="flex items-center text-sm text-gray-600">
                          <FiUser className="mr-2 h-4 w-4" />
                          <span>
                            <strong>Забронировал:</strong> {booking.user.full_name}
                          </span>
                        </div>
                        <div className="flex items-center text-sm text-gray-600">
                          <FiCalendar className="mr-2 h-4 w-4" />
                          <span>
                            <strong>Начало:</strong>{' '}
                            {format(new Date(booking.start_date), 'd MMMM yyyy, HH:mm', { locale: ru })}
                          </span>
                        </div>
                        <div className="flex items-center text-sm text-gray-600">
                          <FiCalendar className="mr-2 h-4 w-4" />
                          <span>
                            <strong>Окончание:</strong>{' '}
                            {format(new Date(booking.end_date), 'd MMMM yyyy, HH:mm', { locale: ru })}
                          </span>
                        </div>
                        <div className="flex items-center text-sm text-gray-600">
                          <FiClock className="mr-2 h-4 w-4" />
                          <span>
                            <strong>Количество:</strong> {booking.quantity} шт
                          </span>
                        </div>
                      </div>

                      <div className="space-y-2">
                        {booking.client_name && (
                          <div className="flex items-center text-sm text-gray-600">
                            <FiUser className="mr-2 h-4 w-4" />
                            <span>
                              <strong>Клиент:</strong> {booking.client_name}
                            </span>
                          </div>
                        )}
                        {booking.deal_id && (
                          <div className="flex items-center text-sm text-gray-600">
                            <FiLink className="mr-2 h-4 w-4" />
                            <span>
                              <strong>Сделка:</strong>{' '}
                              {booking.deal_url ? (
                                <a
                                  href={booking.deal_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-primary-600 hover:text-primary-800 underline"
                                >
                                  #{booking.deal_id}
                                </a>
                              ) : (
                                `#${booking.deal_id}`
                              )}
                            </span>
                          </div>
                        )}
                        {booking.sale_price != null && (
                          <div className="text-sm text-gray-600">
                            <strong>Цена продажи:</strong>{' '}
                            {Number(booking.sale_price).toLocaleString('ru-RU')} ₽ за шт., с НДС
                          </div>
                        )}
                        {booking.notes && (
                          <div className="text-sm text-gray-600">
                            <strong>Примечания:</strong>
                            <p className="mt-1">{booking.notes}</p>
                          </div>
                        )}
                        {booking.auto_completed && (
                          <div className="inline-flex items-center px-3 py-1 bg-indigo-100 text-indigo-800 text-xs font-medium rounded-full">
                            <FiClock className="mr-1" />
                            Завершено по истечении срока
                          </div>
                        )}
                        {isCurrent(booking.start_date, booking.end_date) && booking.status === 'active' && (
                          <div className="inline-flex items-center px-3 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded-full">
                            <FiClock className="mr-1" />
                            Текущее бронирование
                          </div>
                        )}
                        {isUpcoming(booking.start_date) && booking.status === 'active' && (
                          <div className="inline-flex items-center px-3 py-1 bg-yellow-100 text-yellow-800 text-xs font-medium rounded-full">
                            <FiCalendar className="mr-1" />
                            Предстоящее
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {booking.status === 'active' && currentUser && booking.user.id === currentUser.id && (
                    <div className="sm:ml-4 flex flex-wrap sm:justify-end gap-2">
                      {booking.onec_status === 'error' && (
                        <button
                          onClick={() => handleRetryOnec(booking.id)}
                          className="inline-flex items-center px-3 py-2 border border-yellow-300 text-sm font-medium rounded-lg text-yellow-800 bg-white hover:bg-yellow-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 transition-colors"
                        >
                          Отправить в 1С повторно
                        </button>
                      )}
                      <button
                        onClick={() => openEditModal(booking)}
                        className="inline-flex items-center px-3 py-2 border border-blue-300 text-sm font-medium rounded-lg text-blue-700 bg-white hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                      >
                        <FiEdit2 className="mr-1" />
                        Изменить
                      </button>
                      <button
                        onClick={() => handleCancelBooking(booking.id)}
                        className="inline-flex items-center px-3 py-2 border border-red-300 text-sm font-medium rounded-lg text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors"
                      >
                        <FiX className="mr-1" />
                        Отменить
                      </button>
                    </div>
                  )}
                </div>

                <div className="mt-3 ml-6 text-xs text-gray-500">
                  Создано: {format(new Date(booking.created_at), 'd MMMM yyyy, HH:mm', { locale: ru })}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Edit Booking Modal */}
      {editingBooking && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-start justify-center p-4 z-50 overflow-y-auto">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 my-auto">
            <h2 className="text-xl font-bold text-gray-900 mb-4">
              Редактирование бронирования
            </h2>
            
            <div className="mb-4 p-3 bg-gray-50 rounded-lg">
              <p className="font-medium text-gray-900">{editingBooking.equipment.name}</p>
              <p className="text-sm text-gray-600">
                {editingBooking.equipment.vendor} {editingBooking.equipment.model}
              </p>
              {editingBooking.equipment.code_1c && (
                <p className="text-sm text-gray-600 mt-1 flex items-center">
                  <FiHash className="mr-1 h-3.5 w-3.5" />
                  <span className="font-mono">{editingBooking.equipment.code_1c}</span>
                </p>
              )}
              {editingBooking.equipment.price_ddp != null && editingBooking.equipment.price_ddp !== '' && (
                <p className="text-sm text-gray-900 mt-1">
                  <strong>{PRICE_COST_WITHOUT_VAT}:</strong>{' '}
                  {Number(editingBooking.equipment.price_ddp).toLocaleString('ru-RU')} ₽
                </p>
              )}
              {editingBooking.equipment.comment && (
                <p className="text-sm text-gray-600 mt-2 bg-white px-2 py-1 rounded border border-gray-200">
                  <strong>Комментарий:</strong> {editingBooking.equipment.comment}
                </p>
              )}
              {editingBooking.equipment.description && (
                <p className="text-sm text-gray-500 mt-1">
                  {editingBooking.equipment.description}
                </p>
              )}
            </div>

            <form onSubmit={handleUpdateBooking} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Количество (макс. {editingBooking.equipment.available_quantity + editingBooking.quantity})
                </label>
                <input
                  type="number"
                  min="1"
                  max={editingBooking.equipment.available_quantity + editingBooking.quantity}
                  value={editForm.quantity}
                  onChange={(e) =>
                    setEditForm({ ...editForm, quantity: parseInt(e.target.value) })
                  }
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Дата начала
                </label>
                <input
                  type="date"
                  value={editForm.start_date}
                  onChange={(e) =>
                    setEditForm({ ...editForm, start_date: e.target.value })
                  }
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Дата окончания
                </label>
                <input
                  type="date"
                  value={editForm.end_date}
                  onChange={(e) =>
                    setEditForm({ ...editForm, end_date: e.target.value })
                  }
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Имя клиента
                </label>
                <input
                  type="text"
                  value={editForm.client_name}
                  onChange={(e) =>
                    setEditForm({ ...editForm, client_name: e.target.value })
                  }
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="Для какого клиента бронируется"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  ID сделки Bitrix24
                </label>
                <input
                  type="text"
                  value={editForm.deal_id}
                  onChange={(e) =>
                    setEditForm({ ...editForm, deal_id: e.target.value })
                  }
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="12345 или ссылка на сделку"
                />
              </div>

              {(editingBooking?.sale_price != null || editingBooking?.onec_status) && (
                <div>
                  <label htmlFor="edit-sale-price" className="block text-sm font-medium text-gray-700 mb-1">
                    Цена продажи за шт., с НДС, ₽
                  </label>
                  <input
                    id="edit-sale-price"
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={editForm.sale_price ?? ''}
                    onChange={(e) =>
                      setEditForm({ ...editForm, sale_price: e.target.value })
                    }
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Примечания
                </label>
                <textarea
                  value={editForm.notes}
                  onChange={(e) =>
                    setEditForm({ ...editForm, notes: e.target.value })
                  }
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  rows="3"
                  placeholder="Дополнительная информация"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setEditingBooking(null);
                    setEditForm({});
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
                >
                  Отмена
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
                >
                  Сохранить изменения
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Bookings;

